import { FC } from "react";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Button } from "../Button";
import { Spinner } from "../Spinner";
import { DataSource } from "../DataSource";
import styles from "./styles.module.scss";
import { Paged } from "../Paged";

/** The `Pager` component properties. */
export interface PagerProps extends BaseProps {

  /** The data source. */
  readonly dataSource: DataSource<object> & Paged;
}

/** A component which controls paging of data source data. */
export const Pager: FC<PagerProps> = (props) => {
  const { dataSource } = props;
  const { status, busy, page, pageSize, totalRows, maxRows, hasNextPage, first, previous, next, last } = dataSource;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Pager,
      "pager",
      {
        loading: busy,
        ready: status === "success",
      },
    ],
    testIdPrefix: "pager",
  });

  const minimumRow = page * pageSize + 1;
  let maximumRow = (page + 1) * pageSize;
  if (totalRows !== -1 && maximumRow > totalRows) maximumRow = totalRows;

  const pageRange = `${minimumRow} - ${maximumRow}`;

  return (
    <div {...baseHtmlProps} aria-label="Pagination Navigation">
      <Shelf spacing="small" className={styles.PagerContent}>
        <Shelf spacing="small">
          {status !== "initializing" && (
            <div data-testid="pager-results" aria-live="polite" aria-atomic="true" role="status">
              <span className={styles["sr-only"]}>Page navigation results:</span>
              {totalRows > 0 && (
                <>
                  Showing {pageRange} of {totalRows === -1 ? `${maxRows}+` : totalRows} result{totalRows !== 1 ? "s" : ""}
                </>
              )}
            </div>
          )}
          {busy && <Spinner size="regular" />}
        </Shelf>
        <Shelf.Spacer />
        {pageSize < totalRows && (
          <div role="navigation" aria-label="Pagination Controls">
            <Shelf spacing="small" alignment="span">
              <Button appearance="secondary" tooltip="First page" onClick={first} icon="chevron-start" disabled={page === 0} testId="page-to-first" aria-label="Go to first page" />
              <Button appearance="secondary" tooltip="Previous page" onClick={previous} icon="chevron-left" disabled={page === 0} testId="page-to-previous" aria-label="Go to previous page" />
              <Button appearance="secondary" tooltip="Next page" onClick={next} icon="chevron-right" disabled={!hasNextPage} testId="page-to-next" aria-label="Go to next page" />
              <Button appearance="secondary" tooltip="Last page" onClick={last} icon="chevron-end" disabled={!hasNextPage} testId="page-to-last" aria-label="Go to last page" />
            </Shelf>
          </div>
        )
        }
      </Shelf>
    </div>
  );
};
